/**
 * VoiceManager — picker of saved custom voices.
 *
 * The picker shows the avatar's default voice option ("Domyślny głos avatara")
 * plus all custom voices owned by the user. Selecting a voice forwards the
 * ElevenLabs voiceId to the parent so it can be sent to /jobs.
 *
 * NOTE: Tworzenie nowych głosów zostało usunięte — konto ElevenLabs ma twardy
 * limit własnych głosów po API. Picker pokazuje wyłącznie domyślny głos avatara
 * oraz wcześniej zapisane głosy (które wciąż można wybrać i usunąć).
 */
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { ugcStudioApi, type CustomVoice } from "./api";

interface VoiceManagerProps {
  /** Currently selected voiceId or null for "default avatar voice". */
  value: string | null;
  onChange: (voiceId: string | null) => void;
  disabled?: boolean;
}

export function VoiceManager(props: VoiceManagerProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();

  const voicesQuery = useQuery({
    queryKey: ["ugc-voices"],
    queryFn: () => ugcStudioApi.listVoices(),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => ugcStudioApi.deleteVoice(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ugc-voices"] });
      toast({ title: t("ugcStudio.voice.voiceDeleted") });
    },
    onError: (err: Error) =>
      toast({
        title: t("ugcStudio.voice.deleteVoiceFailed"),
        description: err.message,
        variant: "destructive",
      }),
  });

  const voices = voicesQuery.data?.voices ?? [];

  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold text-zinc-200">
        {t("ugcStudio.voice.voiceLabel")}
      </Label>

      <div className="grid grid-cols-1 gap-2">
        <button
          type="button"
          onClick={() => props.onChange(null)}
          disabled={props.disabled}
          className={[
            "flex items-center justify-between rounded-md border p-2.5 text-left transition-colors",
            props.value === null
              ? "border-orange-500 bg-orange-500/10"
              : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700",
          ].join(" ")}
          data-testid="voice-default"
        >
          <div className="space-y-0.5">
            <div className="text-sm text-zinc-100">{t("ugcStudio.voice.defaultVoice")}</div>
            <div className="text-[11px] text-zinc-500">{t("ugcStudio.voice.defaultVoiceHint")}</div>
          </div>
        </button>

        {voicesQuery.isLoading ? (
          <div className="flex items-center justify-center py-3 text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : null}

        {voices.map((voice) => (
          <VoiceRow
            key={voice.id}
            voice={voice}
            isSelected={props.value === voice.voiceId}
            disabled={props.disabled}
            onSelect={() => props.onChange(voice.voiceId)}
            onDelete={() => deleteMut.mutate(voice.id)}
            isDeleting={deleteMut.isPending}
          />
        ))}
      </div>
    </div>
  );
}

interface VoiceRowProps {
  voice: CustomVoice;
  isSelected: boolean;
  disabled?: boolean;
  isDeleting: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

function VoiceRow(props: VoiceRowProps) {
  const { t } = useTranslation();
  return (
    <div
      className={[
        "flex items-center justify-between rounded-md border p-2.5",
        props.isSelected ? "border-orange-500 bg-orange-500/10" : "border-zinc-800 bg-zinc-900/40",
      ].join(" ")}
    >
      <button
        type="button"
        className="flex-1 text-left"
        onClick={props.onSelect}
        disabled={props.disabled}
        data-testid={`voice-row-${props.voice.id}`}
      >
        <div className="text-sm text-zinc-100">{props.voice.name}</div>
        <div className="text-[11px] text-zinc-500 line-clamp-1">{props.voice.prompt}</div>
      </button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        onClick={props.onDelete}
        disabled={props.disabled || props.isDeleting}
        data-testid={`voice-delete-${props.voice.id}`}
        aria-label={t("ugcStudio.voice.deleteVoice")}
      >
        <Trash2 className="h-4 w-4 text-zinc-400" />
      </Button>
    </div>
  );
}
